import { useEffect, useState, useContext } from "react";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import { makeStyles } from "@material-ui/core/styles";
import AccountContext from "../contexts/AccountContext";
import AuthUserContext from "./AuthUserContext";
import { useHistory, useLocation } from "react-router-dom";
import UserMenu from "./UserMenu";
import UserAccountMenu from "./UserAccountMenu";
import AccountMenu from "./navigation/AccountMenu";
import AlbumSearch from "./AlbumSearch";
import isAdmin from "../utils/isAdmin";
import packageData from "../../package.json";
import { Box } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    zIndex: 1200,
    position: "fixed",
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  left: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
  },
  menuIcon: {
    marginRight: theme.spacing(2), // Add spacing after menu icon
  },
  accountMenu: {
    marginLeft: theme.spacing(6), // Add spacing before account menu
  },
  searchContainer: {
    width: 750, // Increased from 250 to 750 (3x wider)
    marginLeft: theme.spacing(10),
    background: "rgba(255, 255, 255, 0.1)",
  },
  appBar: {
    color: "rgb(204, 170, 102)",
    background: "black",
    borderBottom: "5px solid rgb(204, 170, 102)",
  },
  versionPill: {
    fontSize: "0.8em",
    opacity: 0.8,
    backgroundColor: "rgba(0, 0, 0, 0.2)",
    padding: "2px 6px",
    borderRadius: "10px",
    marginLeft: theme.spacing(2),
  },
}));

const Navigation = () => {
  const [siteName, setSiteName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const account = useContext(AccountContext);
  const user = useContext(AuthUserContext);
  const userAccount = user?.accountFull;
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const userIsAdmin = isAdmin();

  // Check if current route is an admin albums route or album preview
  const isAlbumRelatedRoute =
    location.pathname.startsWith("/admin/albums") ||
    location.pathname.includes("/albums/");

  useEffect(() => {
    if (account?.name || account?.nameDisplay) {
      setSiteName(account.nameDisplay || account.name);
    }
  }, [account]);

  // Initialize search query from URL if on admin albums page
  useEffect(() => {
    if (location.pathname.startsWith("/admin/albums") && location.search) {
      const searchParams = new URLSearchParams(location.search);
      const urlQuery = searchParams.get("q");
      if (urlQuery) {
        setSearchQuery(urlQuery);
      }
    }
  }, [location.pathname, location.search]);

  // Search handlers
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleSearch = () => {
    if (searchQuery) {
      // If there's a search query, navigate to search results
      history.push({
        pathname: "/admin/albums",
        search: `?q=${searchQuery}`,
      });
    } else {
      // If search is empty, fetch recent albums
      history.push("/admin/albums");
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    // If on search results page, clear the search and fetch recent albums
    if (location.pathname === "/admin/albums" && location.search) {
      history.push("/admin/albums");
    }
  };

  // Get navbar text color from account settings
  const navbarTextColor = account?.colors?.navbarText || "rgb(204, 170, 102)";

  if (!user) return null;

  return (
    <div className={classes.root}>
      <AppBar
        elevation={2}
        className={classes.appBar}
        style={{
          backgroundColor: account?.colors?.navbarBackground,
          borderBottom: "5px solid " + account?.colors?.navbarPrimary,
          color: navbarTextColor,
        }}
      >
        <Toolbar className={classes.toolbar}>
          <>
            <div className={classes.left}>
              <UserMenu user={user} account={account} />

              <div className={classes.accountMenu}>
                <AccountMenu
                  user={user}
                  account={account}
                  userAccount={userAccount || {}}
                />
              </div>

              {/* Version number with pill background */}
              <Box className={classes.versionPill}>v.{packageData.version}</Box>

              {/* Admin search moved to the left */}
              {userIsAdmin && isAlbumRelatedRoute && (
                <div className={classes.searchContainer}>
                  <AlbumSearch
                    handleChange={handleSearchChange}
                    handleSearch={handleSearch}
                    clearSearch={clearSearch}
                    value={searchQuery}
                    placeholder="Buscar albums..."
                    size="small"
                    inputProps={{
                      style: { color: navbarTextColor },
                    }}
                    // Pass custom styles to match navbar colors
                    customStyles={{
                      inputColor: navbarTextColor,
                      labelColor: navbarTextColor,
                      borderColor: navbarTextColor,
                      iconColor: navbarTextColor,
                    }}
                  />
                </div>
              )}
            </div>

            <div>
              <UserAccountMenu
                distributor={siteName}
                user={user}
                history={history}
                account={account}
              />
            </div>
          </>
        </Toolbar>
      </AppBar>
    </div>
  );
};

export default Navigation;
